import type { Action, ThunkDispatch } from '@reduxjs/toolkit';
import { createAsyncThunk } from '@reduxjs/toolkit';
import type {
  MemoryConfig,
  MemoryConstructionParams,
  PusherEventPayloadMap,
} from '@timelyapp/integration-cloud-api-spec';
import { fetchClient } from '@timelyapp/integration-cloud-sdk/api';
import { coerceMemoryConfigSideEffects } from '@timelyapp/integration-cloud-sdk/utils';
import { debounce } from 'lodash-es';
import type { IState } from 'redux/state';
import debug, { LogLevel } from 'utils/debug';
import { fromISO } from 'utils/luxon-helpers';
import { findLastRelevantMemoryEndedAt } from 'utils/tic/tic-memory-last-ended-at';
import { unionBy } from 'utils/typesafe';
import { safelyDelayRetrainAiModel } from '~domains/ai/predictions/redux/predictions.actions';
import { showToast } from '~redux/modules/toasts.actions';
import { ticApi } from './api-slice/tic.apiSlice';
import { getIsTicAuthenticated } from './tic.auth.selectors';
import { getIsViewingDefaults, setDefaultMemoryConfig } from './tic.config_defaults';
import { getMemoryConfig } from './tic.memories.config.selectors';
import { getMemoryQueryParams } from './tic.memories.selectors';

/**
 * Set MemoryConstructionParams, with optional persistant storage to API
 */
export const setMemoryConfig = createAsyncThunk<
  void,
  Partial<MemoryConstructionParams>,
  { state: IState; dispatch: ThunkDispatch<IState, any, Action> }
>('ticMemories/setMemoryConfig', async (partialQuery, thunkAPI) => {
  const existingQuery = getMemoryConfig(thunkAPI.getState());
  // Get only the filters part of the query
  const { toolConfigsMap, ...filters } = { ...existingQuery, ...partialQuery };

  // Atm, storage happens in ToolConfigModal itself, so we're just looking to update RTK's cache
  thunkAPI.dispatch(ticApi.util.upsertQueryData('getMemoryConfig', undefined, { filters }));
});

/**
 * Internal debounced function to save memory filters to the server
 * This only handles the API call, optimistic updates are done immediately via setMemoryConfig
 */
const internal_debounce_saveMemoryConfig = debounce(
  (
    thunkAPI: { dispatch: ThunkDispatch<IState, any, Action>; getState: () => IState },
    filters: MemoryConfig
  ) => {
    const { dispatch } = thunkAPI;

    // Any memory config change will trigger a re-training of the AI model
    // TODO: Likely fire this on relevant changes only
    const message = 'Memory configuration saved, re-training AI model';
    dispatch(
      showToast({
        type: 'info',
        title: 'DEBUG MEMORY CONFIG CHANGE',
        message,
        logLevel: LogLevel.Debug,
      })
    );
    dispatch(safelyDelayRetrainAiModel({ delayStrategy: 'debounce' }));
    debug.template.ai(message, filters);

    // Make API call
    dispatch(ticApi.endpoints.saveMemoryConfig.initiate({ body: filters }, {}));
  },
  3_000 // 3 seconds
);

/**
 * Save memory filters with debounce to avoid excessive API calls
 * Performs immediate optimistic updates and debounces the actual API call
 */
export const saveMemoryConfig = createAsyncThunk<
  void,
  Partial<MemoryConfig>,
  { state: IState; dispatch: ThunkDispatch<IState, any, Action> }
>('ticMemories/saveMemoryConfig', async (partialFilters, thunkAPI) => {
  const state = thunkAPI.getState();
  // Get the current query
  const existingFilters = getMemoryConfig(state);
  const { memoryConfig: filters } = coerceMemoryConfigSideEffects({
    ...existingFilters,
    ...partialFilters,
  });

  // Local only: Update the default memory config if we're viewing default configs
  // Return early so we don't optimistically update the stored memory configs
  const isViewingDefaults = getIsViewingDefaults(state);
  if (isViewingDefaults) {
    thunkAPI.dispatch(setDefaultMemoryConfig(filters));
    return;
  }

  // Immediate optimistic update of cache
  thunkAPI.dispatch(ticApi.util.upsertQueryData('getMemoryConfig', undefined, { filters }));

  // Debounce the actual API call
  internal_debounce_saveMemoryConfig(thunkAPI, filters);
});

/**
 * Be smart about how we react to Pusher notifications of new memories (from TIC).
 * @ref - PusherCalendarManager.tsx
 */
export const pusherUpdateForTicMemories = createAsyncThunk<
  void,
  PusherEventPayloadMap['new_memories'],
  { state: IState; dispatch: ThunkDispatch<IState, any, Action> }
>('ticMemories/pusherUpdateForTicMemories', async (payload, thunkAPI) => {
  const state = thunkAPI.getState();
  const apiQueryParams = getMemoryQueryParams(state);
  // const memoryQueries = getMemoryConstructionParams(state);
  const isTicAuthenticated = getIsTicAuthenticated(state);

  const timelineFrom = fromISO(apiQueryParams.startDate);
  const timelineTo = fromISO(apiQueryParams.endDate);
  const newMemoriesFrom = fromISO(payload.range_from);
  const newMemoriesTo = fromISO(payload.range_to);

  if (newMemoriesFrom >= timelineFrom && newMemoriesTo <= timelineTo) {
    // Read current memories in RTKQ cache to find the nearest `memory.ended_at` to "now"
    // NB: Intentionally not using getLastRelevantMemoryEndingForAiStatus here (may contain Classic memories)
    // and we can't trust started/fulfilled timestamps from RTKQ since we bypass RTK for partial fetches (below)
    const { data } = ticApi.endpoints.getMemories.select(apiQueryParams)(state);
    const lastMemoryEndedAt = findLastRelevantMemoryEndedAt(data?.memories || [], {
      excludeFuture: true, // We don't want memories in future to cause refetch loop.
    });
    // Assuming we have a last memory, and TIC auth is valid, load only partial memories.
    if (lastMemoryEndedAt && isTicAuthenticated) {
      try {
        // Side-stepping RTKQ here and using ts-rest client directly to simplify implementation.
        // Mixing "partial fetch" with "full fetch" in `getMemories` RTKQ became hard to reason about.
        const startDate = fromISO(lastMemoryEndedAt).minus({ minutes: 15 }).toISO();
        const apiParams = { query: { ...apiQueryParams, startDate } };
        const { body, status } = await fetchClient.memories.getMemories(apiParams);

        if (status === 200) {
          debug.template.memory('New memories fetched, updating cache..', body);
          thunkAPI.dispatch(
            ticApi.util.updateQueryData('getMemories', apiQueryParams, draftMemories => {
              draftMemories.memories = unionBy(draftMemories.memories, body.memories, 'id');
            })
          );
        } else {
          debug.template.memory('Failed partial update, bust cache/full reload.', { body, status });
          thunkAPI.dispatch(ticApi.util.invalidateTags(['Memory']));
        }
      } catch (error) {
        debug.template.memory('Failed partial update, bust cache/full reload.', { error });
        thunkAPI.dispatch(ticApi.util.invalidateTags(['Memory']));
      }
    } else {
      debug.template.memory('No last memory found, bust cache/full reload');
      thunkAPI.dispatch(ticApi.util.invalidateTags(['Memory']));
    }
  } else {
    debug.template.memory('New memories are outside current timeline range, skipping..', payload);
  }
});
